/**
 * Prospecto → cliente cuando su propuesta se gana.
 *
 * Hay DOS disparadores y tienen que compartir candado:
 *
 *  - El explícito: en Estimados se marca la propuesta como Pagada (o Aceptada,
 *    que además crea el proyecto). Ahí hace falta esperar a que el cliente
 *    exista ANTES de seguir, porque el proyecto se crea copiando el client_id
 *    de la propuesta.
 *  - El automático: la propuesta llegó a un estado ganado por fuera de la app
 *    —el cliente la firmó desde el enlace público, que llama a
 *    respond_to_proposal en el servidor—. Nadie en la app pulsó nada, así que
 *    el barrido de abajo lo detecta al refrescar la lista.
 *
 * Sin candado los dos pueden coincidir y crear DOS clientes: el filtro de
 * duplicados de useAddClient lee la caché de clientes, que todavía no sabe nada
 * de la conversión que va en vuelo. Por eso el registro de conversiones vive a
 * nivel de MÓDULO y no en un ref: es una invariante del proceso ("este
 * prospecto se convierte una vez"), no de un componente.
 */
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useIdioma } from '../i18n/idioma';
import { useAuth } from '../contexts/AuthContext';
import { useCRM } from '../contexts/CRMContext';
import { useEstimates } from '../contexts/EstimatesContext';
import { Estimate } from '../types';
import { canManageClients } from '../constants/roles';
import { leadsAwaitingConversion, isLeadProposal } from '../utils/leadProposals';

type Convertir = (leadId: string, opciones?: { porFirma?: boolean }) => Promise<string>;

/** Conversiones lanzadas en esta sesión, por id de prospecto. Las que salen
 *  bien se QUEDAN: así una segunda llamada devuelve el mismo cliente sin
 *  escribir nada. Las que fallan se borran, para poder reintentar. */
const conversiones = new Map<string, Promise<string>>();

/** ¿Ya se lanzó (o completó) la conversión de este prospecto en esta sesión? */
export function conversionYaLanzada(leadId: string): boolean {
    return conversiones.has(leadId);
}

/** Convierte el prospecto una sola vez, compartiendo la promesa entre llamantes. */
export function convertLeadOnce(leadId: string, convertir: Convertir): Promise<string> {
    const enVuelo = conversiones.get(leadId);
    if (enVuelo) return enVuelo;

    const promesa = convertir(leadId).catch(err => {
        conversiones.delete(leadId);
        throw err;
    });
    conversiones.set(leadId, promesa);
    return promesa;
}

/** Solo para pruebas: vacía el registro entre casos. */
export function _resetConversiones() {
    conversiones.clear();
}

export interface EnsureResult {
    /** Id del cliente, o null si la propuesta no venía de un prospecto. */
    clientId: string | null;
    /** La conversión ocurrió AHORA. False cuando ya se había hecho antes en esta
     *  sesión — es lo que evita repetir el aviso sin que haya pasado nada. */
    converted: boolean;
}

/**
 * Garantiza que la propuesta tenga cliente antes de darla por ganada.
 *
 * Devuelve el id del cliente, o null si la propuesta no venía de un prospecto
 * (el caso normal: ya tenía cliente desde el principio).
 *
 * `convertLeadToClient` deja escrito en la base el client_id de la propuesta
 * —y el del proyecto que hubiera generado— antes de resolver, así que quien
 * llame a esto puede seguir con la conversión a proyecto sin más espera.
 */
export function useEnsureLeadClient() {
    const { convertLeadToClient } = useCRM();

    const convertirRef = useRef(convertLeadToClient);
    convertirRef.current = convertLeadToClient;

    const ensureClientForEstimate = async (estimate: Estimate): Promise<EnsureResult> => {
        if (!isLeadProposal(estimate)) return { clientId: null, converted: false };
        const leadId = estimate.sourceLeadId!;
        // Se pregunta ANTES de llamar: `convertLeadOnce` devuelve la promesa ya
        // resuelta a partir de la segunda vez, así que sin esto no habría forma
        // de distinguir «acabo de convertirlo» de «ya estaba», y el aviso salía
        // otra vez al pasar de Pagada a Completada sin que ocurriera nada.
        const yaEstaba = conversionYaLanzada(leadId);
        const clientId = await convertLeadOnce(leadId, id => convertirRef.current(id));
        return { clientId, converted: !yaEstaba };
    };

    return { ensureClientForEstimate };
}

/**
 * Barrido: convierte los prospectos cuyas propuestas ya llegaron a un estado
 * ganado sin que nadie lo pidiera desde la app (firma en el enlace público).
 *
 * Se monta una sola vez, en DataProviders. Un fallo no se reintenta en bucle:
 * `convertLeadOnce` solo suelta el candado cuando falla, y el efecto no vuelve
 * a correr hasta que cambie la lista de propuestas.
 */
export function useLeadProposalAutoConvert() {
    const { t } = useIdioma();
    const { userRole } = useAuth();
    const { leads, convertLeadToClient } = useCRM();
    const { estimates } = useEstimates();

    /* Solo quien puede dar de alta clientes a mano.
       Las políticas RLS solo aíslan por taller y no miran el rol, así que sin
       esto el primero en abrir la app —un Laborer, por ejemplo— era quien creaba
       la ficha, con un aviso de algo que su interfaz ni siquiera le ofrece y que
       no tiene forma de deshacer. No se pierde nada: en cuanto entra alguien de
       oficina, el barrido la crea. */
    const puedeConvertir = canManageClients(userRole);

    // Identidad estable: CRMContext recrea convertLeadToClient en cada render,
    // y como dependencia del efecto lo haría correr sin parar.
    const convertirRef = useRef(convertLeadToClient);
    convertirRef.current = convertLeadToClient;
    const tRef = useRef(t);
    tRef.current = t;

    useEffect(() => {
        if (!puedeConvertir) return;

        const pendientes = leadsAwaitingConversion(estimates)
            .filter(id => !conversionYaLanzada(id))
            // Sin la fila del prospecto no hay datos que copiar al cliente. No
            // debería pasar (la FK pone source_lead_id en NULL al borrarlo),
            // pero la lista puede ir un refresco por detrás.
            .filter(id => leads.some(l => l.id === id));

        if (pendientes.length === 0) return;

        void (async () => {
            for (const leadId of pendientes) {
                const nombre = leads.find(l => l.id === leadId)?.name || '';
                try {
                    /* `porFirma`: esta rama existe porque la propuesta llegó a un
                       estado ganado SIN que nadie pulsara nada en la app —el
                       cliente la firmó desde el enlace público—. Es la única
                       excepción a «un prospecto perdido no salta a Ganado»: ahí
                       el trato está cerrado de verdad, y aplicarle la regla
                       dejaría una propuesta firmada sin ficha de cliente y sin
                       ninguna pantalla para arreglarlo. */
                    await convertLeadOnce(leadId, id => convertirRef.current(id, { porFirma: true }));
                    toast.success(tRef.current('propuestaProspecto.convertido', { nombre }));
                } catch (err: any) {
                    console.error('[leadProposalAutoConvert] falló la conversión de', leadId, err);
                    toast.error(tRef.current('propuestaProspecto.errorConvertir', { nombre }));
                }
            }
        })();
    }, [estimates, leads, puedeConvertir]);
}
